// ============================================================================
// MODULE DATING - SERVICE PRINCIPAL
// Préfixe: dating
// Multi-tenant: tenant_id + domain
// ============================================================================

import { supabase } from '../../lib/supabase';
import { 
  getDomainFilterParams, 
  applyDomainFilterToQuery 
} from '../../hooks/useDomainQuery';
import { sendOtpEmail } from '../emailService';
import {
  DatingProfile,
  DatingProfileInput,
  DatingSuggestion,
  DatingSuggestionsParams,
  DatingInteractionType,
  DatingInteractionResult,
  DatingMatch,
  DatingConversation,
  DatingMessage,
  DatingSendMessageResult,
  DatingPlan,
  DatingSubscription,
  DatingOrder,
  DatingOrderInput,
  DatingDailyLimits,
  DatingProfileStats,
  DatingBlock,
  DatingReport,
  DatingPhoto
} from '../../types/dating';

// ============================================================================
// UTILITAIRES
// ============================================================================

/**
 * Génère un UUID compatible avec tous les navigateurs
 * Fallback si crypto.randomUUID n'est pas disponible
 */
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback pour les navigateurs plus anciens
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// ============================================================================
// PROFILS
// ============================================================================

/**
 * Récupère le profil de l'utilisateur connecté
 */
export const getCurrentDatingProfile = async (): Promise<DatingProfile | null> => {
  const { domain, tenantId } = getDomainFilterParams();
  
  // Essayer d'abord avec le token dating
  const token = localStorage.getItem('dating_access_token');
  if (token) {
    const { data, error } = await supabase
      .from('dating_profiles')
      .select('*')
      .eq('access_token', token)
      .maybeSingle();
    
    if (!error && data) {
      return data as DatingProfile;
    }
  }
  
  // Sinon essayer avec l'utilisateur Supabase Auth
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    let query = supabase
      .from('dating_profiles')
      .select('*')
      .eq('user_id', user.id);
    
    query = applyDomainFilterToQuery(query);
    
    const { data, error } = await query.maybeSingle();
    
    if (!error && data) {
      return data as DatingProfile;
    }
  }
  
  return null;
};

/**
 * Récupère un profil par son ID
 */
export const getDatingProfileById = async (profileId: string): Promise<DatingProfile | null> => {
  const { data, error } = await supabase
    .from('dating_profiles')
    .select('*')
    .eq('id', profileId)
    .single();
  
  if (error) {
    console.error('Erreur lors de la récupération du profil:', error);
    return null;
  }
  
  return data as DatingProfile;
};

/**
 * Crée un nouveau profil Dating
 */
export const createDatingProfile = async (
  input: DatingProfileInput
): Promise<{ profile: DatingProfile | null; error: string | null }> => {
  const { domain, tenantId } = getDomainFilterParams();
  
  // Vérifier si un profil existe déjà pour cet email
  const tenantIdNum = parseInt(tenantId) || 0;
  const { data: existing } = await supabase
    .from('dating_profiles')
    .select('id')
    .eq('email', input.email)
    .eq('tenant_id', tenantIdNum)
    .maybeSingle();
  
  if (existing) {
    return { profile: null, error: 'Un profil existe déjà avec cet email' };
  }
  
  // Construire le point de localisation si fourni
  let location = null;
  if (input.latitude && input.longitude) {
    location = `POINT(${input.longitude} ${input.latitude})`;
  }
  
  // Récupérer l'utilisateur Supabase Auth si connecté
  const { data: { user } } = await supabase.auth.getUser();
  
  // Générer un token d'accès (compatible tous navigateurs)
  const accessToken = generateUUID();
  const tokenExpiresAt = new Date();
  tokenExpiresAt.setDate(tokenExpiresAt.getDate() + 30); // 30 jours
  
  const { data, error } = await supabase
    .from('dating_profiles')
    .insert({
      user_id: user?.id || null,
      tenant_id: tenantIdNum,
      domain,
      display_name: input.display_name,
      email: input.email,
      phone: input.phone,
      bio: input.bio,
      birth_date: input.birth_date,
      gender: input.gender,
      looking_for: input.looking_for,
      location,
      city: input.city,
      country: input.country || 'Cameroun',
      location_visible: input.location_visible ?? true,
      min_age: input.min_age || 18,
      max_age: input.max_age || 99,
      max_distance_km: input.max_distance_km || 50,
      interests: input.interests || [],
      languages: input.languages || ['Français'],
      occupation: input.occupation,
      education: input.education,
      height_cm: input.height_cm,
      access_token: accessToken,
      token_expires_at: tokenExpiresAt.toISOString()
    })
    .select()
    .single();
  
  if (error) {
    console.error('Erreur lors de la création du profil:', error);
    return { profile: null, error: error.message };
  }
  
  // Stocker le token
  localStorage.setItem('dating_access_token', accessToken);
  
  return { profile: data as DatingProfile, error: null };
};

/**
 * Met à jour le profil Dating
 */
export const updateDatingProfile = async (
  profileId: string,
  input: Partial<DatingProfileInput>
): Promise<{ profile: DatingProfile | null; error: string | null }> => {
  const updateData: Record<string, any> = { ...input };
  
  // Construire le point de localisation si fourni
  if (input.latitude !== undefined && input.longitude !== undefined) {
    updateData.location = `POINT(${input.longitude} ${input.latitude})`;
    delete updateData.latitude;
    delete updateData.longitude;
  }
  
  updateData.updated_at = new Date().toISOString();
  
  const { data, error } = await supabase
    .from('dating_profiles')
    .update(updateData)
    .eq('id', profileId)
    .select()
    .single();
  
  if (error) {
    console.error('Erreur lors de la mise à jour du profil:', error);
    return { profile: null, error: error.message };
  }
  
  return { profile: data as DatingProfile, error: null };
};

/**
 * Met à jour la dernière activité du profil
 */
export const updateLastActive = async (profileId: string): Promise<void> => {
  await supabase
    .from('dating_profiles')
    .update({ 
      last_active_at: new Date().toISOString(),
      is_online: true 
    })
    .eq('id', profileId);
};

/**
 * Marque le profil comme hors ligne
 */
export const setOffline = async (profileId: string): Promise<void> => {
  await supabase
    .from('dating_profiles')
    .update({ is_online: false })
    .eq('id', profileId);
};

// ============================================================================
// PHOTOS
// ============================================================================

/**
 * Upload une photo de profil
 */
export const uploadDatingPhoto = async (
  profileId: string,
  file: File,
  isPrimary: boolean = false
): Promise<{ url: string | null; error: string | null }> => {
  // Générer un nom de fichier unique
  const fileExt = file.name.split('.').pop()?.toLowerCase();
  const fileName = `dating_${profileId}_${Date.now()}.${fileExt}`;
  // Même structure que elearning: avatars/{profileId}/{fileName}
  // Avec préfixe "dating_" pour identifier les photos dating
  const filePath = `avatars/${profileId}/${fileName}`;
  
  // Upload vers Supabase Storage (même bucket que elearning)
  const { data: uploadData, error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true
    });
  
  if (uploadError) {
    console.error('[uploadDatingPhoto] Erreur upload:', uploadError);
    return { url: null, error: uploadError.message };
  }
  
  // Récupérer l'URL publique
  const { data: { publicUrl } } = supabase.storage
    .from('avatars')
    .getPublicUrl(filePath);
  
  // Récupérer les photos existantes
  const { data: profile } = await supabase
    .from('dating_profiles')
    .select('photos')
    .eq('id', profileId)
    .single();
  
  const existingPhotos: DatingPhoto[] = profile?.photos || [];
  
  // Si c'est la photo principale, mettre les autres en non-principal
  const updatedPhotos = existingPhotos.map(p => ({
    ...p,
    is_primary: isPrimary ? false : p.is_primary
  }));
  
  // Ajouter la nouvelle photo
  updatedPhotos.push({
    url: publicUrl,
    is_primary: isPrimary || updatedPhotos.length === 0,
    order: updatedPhotos.length,
    uploaded_at: new Date().toISOString()
  });
  
  // Mettre à jour le profil
  const updateData: Record<string, any> = { photos: updatedPhotos };
  if (isPrimary || existingPhotos.length === 0) {
    updateData.avatar_url = publicUrl;
  }
  
  await supabase
    .from('dating_profiles')
    .update(updateData)
    .eq('id', profileId);
  
  return { url: publicUrl, error: null };
};

/**
 * Supprime une photo de profil
 */
export const deleteDatingPhoto = async (
  profileId: string,
  photoUrl: string
): Promise<{ success: boolean; error: string | null }> => {
  // Récupérer les photos existantes
  const { data: profile } = await supabase
    .from('dating_profiles')
    .select('photos, avatar_url')
    .eq('id', profileId)
    .single();
  
  if (!profile) {
    return { success: false, error: 'Profil non trouvé' };
  }
  
  const existingPhotos: DatingPhoto[] = profile.photos || [];
  const photoToDelete = existingPhotos.find(p => p.url === photoUrl);
  
  if (!photoToDelete) {
    return { success: false, error: 'Photo non trouvée' };
  }
  
  // Supprimer du storage
  const path = photoUrl.split('/avatars/')[1];
  if (path) {
    await supabase.storage.from('avatars').remove([path]);
  }
  
  // Mettre à jour la liste des photos
  const updatedPhotos = existingPhotos
    .filter(p => p.url !== photoUrl)
    .map((p, index) => ({ ...p, order: index }));
  
  // Si c'était la photo principale, définir la première comme principale
  const updateData: Record<string, any> = { photos: updatedPhotos };
  
  if (photoToDelete.is_primary && updatedPhotos.length > 0) {
    updatedPhotos[0].is_primary = true;
    updateData.avatar_url = updatedPhotos[0].url;
  } else if (updatedPhotos.length === 0) {
    updateData.avatar_url = null;
  }
  
  await supabase
    .from('dating_profiles')
    .update(updateData)
    .eq('id', profileId);
  
  return { success: true, error: null };
};

/**
 * Définit une photo comme principale
 */
export const setDatingPrimaryPhoto = async (
  profileId: string,
  photoUrl: string
): Promise<{ success: boolean; error: string | null }> => {
  const { data: profile } = await supabase
    .from('dating_profiles')
    .select('photos')
    .eq('id', profileId)
    .single();
  
  if (!profile) {
    return { success: false, error: 'Profil non trouvé' };
  }
  
  const updatedPhotos = (profile.photos as DatingPhoto[]).map(p => ({
    ...p,
    is_primary: p.url === photoUrl
  }));
  
  // Mettre à jour photos ET avatar_url (utilisé pour l'affichage dans les listes)
  const { error } = await supabase
    .from('dating_profiles')
    .update({ 
      photos: updatedPhotos,
      avatar_url: photoUrl,
      updated_at: new Date().toISOString()
    })
    .eq('id', profileId);
  
  if (error) {
    console.error('[setDatingPrimaryPhoto] Erreur:', error);
    return { success: false, error: error.message };
  }
  
  console.log('[setDatingPrimaryPhoto] Photo principale mise à jour:', photoUrl);
  return { success: true, error: null };
};

// ============================================================================
// SUGGESTIONS (SWIPE)
// ============================================================================

/**
 * Récupère les suggestions de profils pour le swipe
 */
export const getDatingSuggestions = async (
  profileId: string,
  params: DatingSuggestionsParams = {}
): Promise<DatingSuggestion[]> => {
  const { data, error } = await supabase.rpc('dating_get_suggestions', {
    p_profile_id: profileId,
    p_latitude: params.latitude || null,
    p_longitude: params.longitude || null,
    p_radius_km: params.radius_km || 50,
    p_limit: params.limit || 20,
    p_offset: params.offset || 0
  });
  
  if (error) {
    console.error('Erreur lors de la récupération des suggestions:', error);
    return [];
  }
  
  return data as DatingSuggestion[];
};

// ============================================================================
// INTERACTIONS (LIKE/DISLIKE/SUPER_LIKE)
// ============================================================================

/**
 * Crée une interaction (like, dislike, super_like)
 */
export const createDatingInteraction = async (
  fromProfileId: string,
  toProfileId: string,
  interactionType: DatingInteractionType
): Promise<DatingInteractionResult> => {
  const { domain, tenantId } = getDomainFilterParams();
  
  const { data, error } = await supabase.rpc('dating_create_interaction', {
    p_from_profile_id: fromProfileId,
    p_to_profile_id: toProfileId,
    p_interaction_type: interactionType,
    p_tenant_id: parseInt(tenantId),
    p_domain: domain
  });
  
  if (error) {
    console.error('Erreur lors de la création de l\'interaction:', error);
    return { success: false, is_match: false, error: error.message };
  }
  
  return data as DatingInteractionResult;
};

/**
 * Récupère les profils qui ont liké l'utilisateur (VIP uniquement)
 */
export const getWhoLikedMe = async (
  profileId: string,
  limit: number = 20,
  offset: number = 0
): Promise<DatingProfile[]> => {
  const { data, error } = await supabase
    .from('dating_interactions')
    .select(`
      from_profile_id,
      dating_profiles!dating_interactions_from_profile_id_fkey (*)
    `)
    .eq('to_profile_id', profileId)
    .in('interaction_type', ['like', 'super_like'])
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  
  if (error) {
    console.error('Erreur lors de la récupération des likes:', error);
    return [];
  }
  
  return data.map((d: any) => d.dating_profiles) as DatingProfile[];
};

// ============================================================================
// MATCHES
// ============================================================================

/**
 * Récupère les matches de l'utilisateur
 */
export const getDatingMatches = async (
  profileId: string
): Promise<DatingMatch[]> => {
  const { data, error } = await supabase
    .from('dating_matches')
    .select(`
      *,
      profile1:dating_profiles!dating_matches_profile1_id_fkey (*),
      profile2:dating_profiles!dating_matches_profile2_id_fkey (*),
      conversation:dating_conversations!dating_conversations_match_id_fkey (*)
    `)
    .or(`profile1_id.eq.${profileId},profile2_id.eq.${profileId}`)
    .eq('status', 'active')
    .order('matched_at', { ascending: false });
  
  if (error) {
    console.error('Erreur lors de la récupération des matches:', error);
    return [];
  }
  
  return data as DatingMatch[];
};

/**
 * Unmatch un profil
 */
export const unmatchDatingProfile = async (
  matchId: string,
  profileId: string
): Promise<{ success: boolean; error: string | null }> => {
  const { error } = await supabase
    .from('dating_matches')
    .update({
      status: 'unmatched',
      unmatched_at: new Date().toISOString(),
      unmatched_by: profileId
    })
    .eq('id', matchId);
  
  if (error) {
    return { success: false, error: error.message };
  }
  
  return { success: true, error: null };
};

// ============================================================================
// CONVERSATIONS & MESSAGES
// ============================================================================

/**
 * Récupère les conversations de l'utilisateur
 */
export const getDatingConversations = async (
  profileId: string
): Promise<DatingConversation[]> => {
  const { data, error } = await supabase
    .from('dating_conversations')
    .select(`
      *,
      participant1:dating_profiles!dating_conversations_participant1_id_fkey (
        id, display_name, avatar_url, is_online, last_active_at
      ),
      participant2:dating_profiles!dating_conversations_participant2_id_fkey (
        id, display_name, avatar_url, is_online, last_active_at
      )
    `)
    .or(`participant1_id.eq.${profileId},participant2_id.eq.${profileId}`)
    .eq('is_active', true)
    .order('last_message_at', { ascending: false, nullsFirst: false });
  
  if (error) {
    console.error('Erreur lors de la récupération des conversations:', error);
    return [];
  }
  
  return data as DatingConversation[];
};

/**
 * Récupère une conversation par son ID
 */
export const getDatingConversationById = async (
  conversationId: string
): Promise<DatingConversation | null> => {
  const { data, error } = await supabase
    .from('dating_conversations')
    .select(`
      *,
      participant1:dating_profiles!dating_conversations_participant1_id_fkey (
        id, display_name, avatar_url, is_online, last_active_at, photos, is_vip, is_verified, city, birth_date
      ),
      participant2:dating_profiles!dating_conversations_participant2_id_fkey (
        id, display_name, avatar_url, is_online, last_active_at, photos, is_vip, is_verified, city, birth_date
      )
    `)
    .eq('id', conversationId)
    .single();
  
  if (error) {
    console.error('Erreur lors de la récupération de la conversation:', error);
    return null;
  }
  
  return data as DatingConversation;
};

/**
 * Récupère les messages d'une conversation
 */
export const getDatingMessages = async (
  conversationId: string,
  limit: number = 50,
  before?: string
): Promise<DatingMessage[]> => {
  let query = supabase
    .from('dating_messages')
    .select(`
      *,
      sender:dating_profiles!dating_messages_sender_id_fkey (
        id, display_name, avatar_url
      )
    `)
    .eq('conversation_id', conversationId)
    .eq('is_deleted', false)
    .order('sent_at', { ascending: false })
    .limit(limit);
  
  if (before) {
    query = query.lt('sent_at', before);
  }
  
  const { data, error } = await query;
  
  if (error) {
    console.error('Erreur lors de la récupération des messages:', error);
    return [];
  }
  
  // Retourner dans l'ordre chronologique
  return (data as DatingMessage[]).reverse();
};

/**
 * Envoie un message
 */
export const sendDatingMessage = async (
  conversationId: string,
  senderId: string,
  content: string,
  messageType: 'text' | 'image' | 'gif' | 'audio' = 'text',
  mediaUrl?: string
): Promise<DatingSendMessageResult> => {
  const { data, error } = await supabase.rpc('dating_send_message', {
    p_conversation_id: conversationId,
    p_sender_id: senderId,
    p_content: content,
    p_message_type: messageType,
    p_media_url: mediaUrl || null
  });
  
  if (error) {
    console.error('Erreur lors de l\'envoi du message:', error);
    return { success: false, error: error.message };
  }
  
  return data as DatingSendMessageResult;
};

/**
 * Marque les messages comme lus
 */
export const markDatingMessagesAsRead = async (
  conversationId: string,
  readerId: string
): Promise<void> => {
  await supabase.rpc('dating_mark_messages_read', {
    p_conversation_id: conversationId,
    p_reader_id: readerId
  });
};

// ============================================================================
// PLANS & ABONNEMENTS
// ============================================================================

/**
 * Récupère les plans disponibles
 */
export const getDatingPlans = async (): Promise<DatingPlan[]> => {
  let query = supabase
    .from('dating_plans')
    .select('*')
    .eq('is_active', true)
    .order('display_order', { ascending: true });
  
  query = applyDomainFilterToQuery(query);
  
  const { data, error } = await query;
  
  if (error) {
    console.error('Erreur lors de la récupération des plans:', error);
    return [];
  }
  
  return data as DatingPlan[];
};

/**
 * Récupère l'abonnement actif de l'utilisateur
 */
export const getActiveDatingSubscription = async (
  profileId: string
): Promise<DatingSubscription | null> => {
  const { data, error } = await supabase
    .from('dating_subscriptions')
    .select(`
      *,
      plan:dating_plans (*)
    `)
    .eq('profile_id', profileId)
    .eq('is_active', true)
    .order('expires_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  
  if (error) {
    console.error('[getActiveDatingSubscription] Erreur:', error);
    return null;
  }
  
  return data as DatingSubscription | null;
};

// ============================================================================
// UPGRADE - CALCUL DES OPTIONS
// ============================================================================

/**
 * Interface pour les options d'upgrade
 */
export interface UpgradeOptions {
  hasActiveSubscription: boolean;
  currentPlan: DatingPlan | null;
  newPlan: DatingPlan | null;
  remainingDays: number;
  remainingCredit: number;
  // Option 1: Ajouter du temps
  addTimeOption: {
    bonusDays: number;
    totalDays: number;
    priceToPay: number;
  };
  // Option 2: Réduire le prix
  reducePriceOption: {
    originalPrice: number;
    discount: number;
    priceToPay: number;
    totalDays: number;
  };
}

/**
 * Calcule les options d'upgrade disponibles
 * Fonctionne pour tout plan, quelle que soit la durée ou le prix
 */
export const calculateUpgradeOptions = (
  subscription: DatingSubscription | null,
  newPlan: DatingPlan
): UpgradeOptions => {
  // Pas d'abonnement actif = achat normal
  if (!subscription || subscription.status !== 'active' || !subscription.plan) {
    return {
      hasActiveSubscription: false,
      currentPlan: null,
      newPlan,
      remainingDays: 0,
      remainingCredit: 0,
      addTimeOption: {
        bonusDays: 0,
        totalDays: newPlan.duration_days,
        priceToPay: newPlan.price
      },
      reducePriceOption: {
        originalPrice: newPlan.price,
        discount: 0,
        priceToPay: newPlan.price,
        totalDays: newPlan.duration_days
      }
    };
  }

  const currentPlan = subscription.plan;
  
  // Même plan = renouvellement simple (pas d'upgrade)
  if (currentPlan.id === newPlan.id) {
    return {
      hasActiveSubscription: true,
      currentPlan,
      newPlan,
      remainingDays: 0,
      remainingCredit: 0,
      addTimeOption: {
        bonusDays: 0,
        totalDays: newPlan.duration_days,
        priceToPay: newPlan.price
      },
      reducePriceOption: {
        originalPrice: newPlan.price,
        discount: 0,
        priceToPay: newPlan.price,
        totalDays: newPlan.duration_days
      }
    };
  }

  // Calculer le temps restant
  const now = new Date();
  const expiresAt = new Date(subscription.expires_at);
  const remainingMs = Math.max(0, expiresAt.getTime() - now.getTime());
  const remainingDays = remainingMs / (1000 * 60 * 60 * 24);

  // Calculer le crédit restant (valeur monétaire du temps restant)
  const currentDailyRate = currentPlan.price / currentPlan.duration_days;
  const remainingCredit = remainingDays * currentDailyRate;

  // Option 1: Ajouter du temps - convertir le crédit en jours du nouveau plan
  const newDailyRate = newPlan.price / newPlan.duration_days;
  const bonusDays = Math.ceil(remainingCredit / newDailyRate); // Arrondi au supérieur (généreux)

  // Option 2: Réduire le prix - déduire le crédit du prix
  const discount = Math.min(remainingCredit, newPlan.price); // Ne pas dépasser le prix
  const reducedPrice = Math.max(0, newPlan.price - discount);

  return {
    hasActiveSubscription: true,
    currentPlan,
    newPlan,
    remainingDays: Math.round(remainingDays * 10) / 10, // Arrondi à 1 décimale
    remainingCredit: Math.round(remainingCredit), // Arrondi à l'entier
    addTimeOption: {
      bonusDays,
      totalDays: newPlan.duration_days + bonusDays,
      priceToPay: newPlan.price
    },
    reducePriceOption: {
      originalPrice: newPlan.price,
      discount: Math.round(discount),
      priceToPay: Math.round(reducedPrice),
      totalDays: newPlan.duration_days
    }
  };
};

// ============================================================================
// COMMANDES
// ============================================================================

/**
 * Input étendu pour les commandes avec upgrade et coupon
 */
export interface DatingOrderInputExtended extends DatingOrderInput {
  upgrade_from_subscription_id?: string;
  upgrade_credit_amount?: number;
  upgrade_option?: 'add_time' | 'reduce_price';
  upgrade_bonus_days?: number;
  coupon_discount?: number; // Réduction du coupon (déjà calculée)
}

/**
 * Crée une commande pour un plan (avec support upgrade)
 */
export const createDatingOrder = async (
  profileId: string,
  input: DatingOrderInputExtended
): Promise<{ order: DatingOrder | null; error: string | null }> => {
  const { domain, tenantId } = getDomainFilterParams();
  
  // Récupérer le plan
  const { data: plan } = await supabase
    .from('dating_plans')
    .select('*')
    .eq('id', input.plan_id)
    .single();
  
  if (!plan) {
    return { order: null, error: 'Plan non trouvé' };
  }
  
  // Calculer le montant avec les réductions
  let amount = plan.price;
  let discountAmount = 0;
  
  // Appliquer la réduction d'upgrade si option "reduce_price"
  if (input.upgrade_option === 'reduce_price' && input.upgrade_credit_amount) {
    discountAmount += input.upgrade_credit_amount;
    amount = Math.max(0, amount - input.upgrade_credit_amount);
  }
  
  // Appliquer la réduction du coupon (déjà validé côté frontend)
  if (input.coupon_discount && input.coupon_discount > 0) {
    discountAmount += input.coupon_discount;
    amount = Math.max(0, amount - input.coupon_discount);
  }
  
  const { data, error } = await supabase
    .from('dating_orders')
    .insert({
      profile_id: profileId,
      plan_id: input.plan_id,
      tenant_id: parseInt(tenantId),
      domain,
      customer_name: input.customer_name,
      customer_email: input.customer_email,
      customer_phone: input.customer_phone,
      amount,
      currency: plan.currency,
      coupon_code: input.coupon_code,
      discount_amount: discountAmount,
      payment_method: input.payment_method,
      status: 'pending',
      // Champs d'upgrade
      upgrade_from_subscription_id: input.upgrade_from_subscription_id || null,
      upgrade_credit_amount: input.upgrade_credit_amount || 0,
      upgrade_option: input.upgrade_option || null,
      upgrade_bonus_days: input.upgrade_bonus_days || 0
    })
    .select()
    .single();
  
  if (error) {
    console.error('Erreur lors de la création de la commande:', error);
    return { order: null, error: error.message };
  }
  
  return { order: data as DatingOrder, error: null };
};

/**
 * Récupère une commande par son ID
 */
export const getDatingOrderById = async (
  orderId: string
): Promise<DatingOrder | null> => {
  const { data, error } = await supabase
    .from('dating_orders')
    .select(`
      *,
      plan:dating_plans (*)
    `)
    .eq('id', orderId)
    .single();
  
  if (error) {
    return null;
  }
  
  return data as DatingOrder;
};

/**
 * Récupère les commandes d'un profil
 */
export const getDatingOrders = async (
  profileId: string
): Promise<DatingOrder[]> => {
  const { data, error } = await supabase
    .from('dating_orders')
    .select(`
      *,
      plan:dating_plans (*)
    `)
    .eq('profile_id', profileId)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Erreur lors de la récupération des commandes:', error);
    return [];
  }
  
  return data as DatingOrder[];
};

// ============================================================================
// LIMITES QUOTIDIENNES
// ============================================================================

/**
 * Récupère les limites quotidiennes
 */
export const getDatingDailyLimits = async (
  profileId: string
): Promise<DatingDailyLimits | null> => {
  const today = new Date().toISOString().split('T')[0];
  
  const { data, error } = await supabase
    .from('dating_daily_limits')
    .select('*')
    .eq('profile_id', profileId)
    .eq('date', today)
    .maybeSingle();
  
  if (error) {
    console.error('[getDatingDailyLimits] Erreur:', error);
    return null;
  }
  
  return data as DatingDailyLimits | null;
};

// ============================================================================
// BLOCAGES
// ============================================================================

/**
 * Bloque un profil
 */
export const blockDatingProfile = async (
  blockerId: string,
  blockedId: string,
  reason?: string
): Promise<{ success: boolean; error: string | null }> => {
  const { domain, tenantId } = getDomainFilterParams();
  
  const { error } = await supabase
    .from('dating_blocks')
    .insert({
      blocker_id: blockerId,
      blocked_id: blockedId,
      tenant_id: parseInt(tenantId),
      domain,
      reason
    });
  
  if (error) {
    return { success: false, error: error.message };
  }
  
  return { success: true, error: null };
};

/**
 * Débloque un profil
 */
export const unblockDatingProfile = async (
  blockerId: string,
  blockedId: string
): Promise<{ success: boolean; error: string | null }> => {
  const { error } = await supabase
    .from('dating_blocks')
    .delete()
    .eq('blocker_id', blockerId)
    .eq('blocked_id', blockedId);
  
  if (error) {
    return { success: false, error: error.message };
  }
  
  return { success: true, error: null };
};

/**
 * Récupère la liste des profils bloqués
 */
export const getBlockedDatingProfiles = async (
  blockerId: string
): Promise<DatingBlock[]> => {
  const { data, error } = await supabase
    .from('dating_blocks')
    .select(`
      *,
      blocked:dating_profiles!dating_blocks_blocked_id_fkey (
        id, display_name, avatar_url
      )
    `)
    .eq('blocker_id', blockerId)
    .order('created_at', { ascending: false });
  
  if (error) {
    console.error('Erreur lors de la récupération des blocages:', error);
    return [];
  }
  
  return data as DatingBlock[];
};

/**
 * Vérifie si un blocage existe entre deux profils (dans les deux sens)
 * Retourne: { isBlocked: boolean, blockedBy: 'me' | 'them' | null }
 */
export const checkDatingBlock = async (
  myProfileId: string,
  otherProfileId: string
): Promise<{ isBlocked: boolean; blockedBy: 'me' | 'them' | null }> => {
  const { data, error } = await supabase
    .from('dating_blocks')
    .select('blocker_id, blocked_id')
    .or(`and(blocker_id.eq.${myProfileId},blocked_id.eq.${otherProfileId}),and(blocker_id.eq.${otherProfileId},blocked_id.eq.${myProfileId})`);
  
  if (error) {
    console.error('Erreur vérification blocage:', error);
    return { isBlocked: false, blockedBy: null };
  }
  
  if (!data || data.length === 0) {
    return { isBlocked: false, blockedBy: null };
  }
  
  // Vérifier qui a bloqué qui
  const block = data[0];
  if (block.blocker_id === myProfileId) {
    return { isBlocked: true, blockedBy: 'me' };
  } else {
    return { isBlocked: true, blockedBy: 'them' };
  }
};

// ============================================================================
// SIGNALEMENTS
// ============================================================================

/**
 * Signale un profil
 */
export const reportDatingProfile = async (
  reporterId: string,
  reportedId: string,
  reason: string,
  description?: string,
  evidenceUrls?: string[]
): Promise<{ success: boolean; error: string | null }> => {
  const { domain, tenantId } = getDomainFilterParams();
  
  const { error } = await supabase
    .from('dating_reports')
    .insert({
      reporter_id: reporterId,
      reported_id: reportedId,
      tenant_id: parseInt(tenantId),
      domain,
      reason,
      description,
      evidence_urls: evidenceUrls || []
    });
  
  if (error) {
    return { success: false, error: error.message };
  }
  
  return { success: true, error: null };
};

// ============================================================================
// STATISTIQUES
// ============================================================================

/**
 * Récupère les statistiques du profil
 */
export const getDatingProfileStats = async (
  profileId: string
): Promise<DatingProfileStats> => {
  // Récupérer le profil pour les stats de base
  const { data: profile } = await supabase
    .from('dating_profiles')
    .select('total_likes_received, total_likes_given, total_matches, total_super_likes_received, profile_views')
    .eq('id', profileId)
    .single();
  
  // Compter les conversations actives
  const { count: activeConversations } = await supabase
    .from('dating_conversations')
    .select('*', { count: 'exact', head: true })
    .or(`participant1_id.eq.${profileId},participant2_id.eq.${profileId}`)
    .eq('is_active', true);
  
  // Compter les messages non lus
  const { data: conversations } = await supabase
    .from('dating_conversations')
    .select('participant1_id, unread_count_p1, unread_count_p2')
    .or(`participant1_id.eq.${profileId},participant2_id.eq.${profileId}`);
  
  let unreadMessages = 0;
  conversations?.forEach((conv: any) => {
    if (conv.participant1_id === profileId) {
      unreadMessages += conv.unread_count_p1 || 0;
    } else {
      unreadMessages += conv.unread_count_p2 || 0;
    }
  });
  
  return {
    total_likes_received: profile?.total_likes_received || 0,
    total_likes_given: profile?.total_likes_given || 0,
    total_matches: profile?.total_matches || 0,
    total_super_likes_received: profile?.total_super_likes_received || 0,
    profile_views: profile?.profile_views || 0,
    active_conversations: activeConversations || 0,
    unread_messages: unreadMessages
  };
};

/**
 * Enregistre une vue de profil (incrémente le compteur)
 * Ne compte pas si c'est son propre profil ou si déjà vu récemment
 */
export const recordDatingProfileView = async (
  viewerId: string,
  viewedProfileId: string
): Promise<boolean> => {
  // Ne pas compter si c'est son propre profil
  if (viewerId === viewedProfileId) {
    return false;
  }

  // Vérifier si déjà vu dans les dernières 24h (éviter spam)
  const cacheKey = `dating_view_${viewerId}_${viewedProfileId}`;
  const lastView = localStorage.getItem(cacheKey);
  const now = Date.now();
  
  if (lastView && now - parseInt(lastView) < 24 * 60 * 60 * 1000) {
    return false; // Déjà vu dans les 24h
  }

  try {
    // Incrémenter le compteur de vues
    const { error } = await supabase.rpc('increment_profile_views', {
      profile_id: viewedProfileId
    });

    if (error) {
      // Fallback: mise à jour directe si la fonction RPC n'existe pas
      const { data: profile } = await supabase
        .from('dating_profiles')
        .select('profile_views')
        .eq('id', viewedProfileId)
        .single();
      
      if (profile) {
        await supabase
          .from('dating_profiles')
          .update({ profile_views: (profile.profile_views || 0) + 1 })
          .eq('id', viewedProfileId);
      }
    }

    // Sauvegarder dans le cache local
    localStorage.setItem(cacheKey, now.toString());
    return true;
  } catch (err) {
    console.error('Erreur enregistrement vue profil:', err);
    return false;
  }
};

// ============================================================================
// AUTHENTIFICATION
// ============================================================================

/**
 * Génère un token d'accès pour un profil (OTP)
 */
export const generateDatingAccessToken = async (
  email: string
): Promise<{ profile: DatingProfile | null; error: string | null }> => {
  const { domain, tenantId } = getDomainFilterParams();
  const tenantIdNum = parseInt(tenantId) || 0;
  
  // Chercher le profil existant
  const { data: existing, error: fetchError } = await supabase
    .from('dating_profiles')
    .select('*')
    .eq('email', email)
    .eq('tenant_id', tenantIdNum)
    .maybeSingle();
  
  if (fetchError) {
    console.error('[generateDatingAccessToken] Erreur fetch:', fetchError);
    return { profile: null, error: fetchError.message };
  }
  
  if (!existing) {
    return { profile: null, error: 'Aucun profil trouvé avec cet email' };
  }
  
  // Générer un nouveau token (compatible tous navigateurs)
  const accessToken = generateUUID();
  const tokenExpiresAt = new Date();
  tokenExpiresAt.setDate(tokenExpiresAt.getDate() + 30);
  
  const { data, error } = await supabase
    .from('dating_profiles')
    .update({
      access_token: accessToken,
      token_expires_at: tokenExpiresAt.toISOString(),
      last_active_at: new Date().toISOString()
    })
    .eq('id', existing.id)
    .select()
    .maybeSingle();
  
  if (error) {
    console.error('[generateDatingAccessToken] Erreur update:', error);
    return { profile: null, error: error.message };
  }
  
  if (!data) {
    // L'update a réussi mais pas de données retournées (RLS ou autre)
    // On stocke quand même le token et on retourne le profil existant avec le nouveau token
    localStorage.setItem('dating_access_token', accessToken);
    return { 
      profile: { ...existing, access_token: accessToken, token_expires_at: tokenExpiresAt.toISOString() } as DatingProfile, 
      error: null 
    };
  }
  
  // Stocker le token
  localStorage.setItem('dating_access_token', accessToken);
  
  return { profile: data as DatingProfile, error: null };
};

/**
 * Déconnexion
 */
export const logoutDating = (): void => {
  localStorage.removeItem('dating_access_token');
};

/**
 * Vérifie si l'utilisateur est connecté
 */
export const isDatingAuthenticated = (): boolean => {
  return !!localStorage.getItem('dating_access_token');
};

// ============================================================================
// OTP DATING (FRONT-SEUL, COMME ELEARNING)
// ============================================================================

const DATING_OTP_STORAGE_PREFIX = 'dating_otp_';

const generateDatingOTP = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

export const sendDatingOTP = async (email: string): Promise<boolean> => {
  try {
    const otp = generateDatingOTP();

    // Stocker l'OTP dans localStorage (10 minutes)
    localStorage.setItem(`${DATING_OTP_STORAGE_PREFIX}${email}`, otp);
    localStorage.setItem(
      `${DATING_OTP_STORAGE_PREFIX}expiry_${email}`,
      (Date.now() + 10 * 60 * 1000).toString()
    );

    // Envoyer l'email OTP via le système existant
    await sendOtpEmail({
      to: email,
      name: email,
      otpCode: otp,
      context: 'dating'
    });

    return true;
  } catch (error) {
    console.error('[sendDatingOTP] Erreur lors de l\'envoi du code OTP:', error);
    return false;
  }
};

export const verifyDatingOTP = (email: string, otp: string): boolean => {
  const storedOTP = localStorage.getItem(`${DATING_OTP_STORAGE_PREFIX}${email}`);
  const expiryTime = localStorage.getItem(`${DATING_OTP_STORAGE_PREFIX}expiry_${email}`);

  if (!storedOTP || !expiryTime) {
    return false;
  }

  if (parseInt(expiryTime, 10) < Date.now()) {
    return false;
  }

  return storedOTP === otp;
};
